import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GraduationCap, Eye, Edit, Trash, Filter, Users } from "lucide-react";
import dayjs from "dayjs";
import { Select } from "antd";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import {
    TRAINING_TASKS,
    TRAINING_MY_TASKS,
    TRAINING_TEAM_TASKS,
    EMPLOYEE_DEPARTMENT,
} from "../../constants/api";

const statusStyles = {
    pending: "bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-200",
    in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    overdue: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

const statusLabels = {
    pending: "Pending",
    in_progress: "In Progress",
    completed: "Completed",
    overdue: "Overdue",
};

const priorityStyles = {
    low: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    high: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
};

const StatusPill = ({ value }) => (
    <span className={cn("px-2 py-0.5 text-xs font-medium rounded-full", statusStyles[value] || statusStyles.pending)}>
        {statusLabels[value] || value}
    </span>
);

const PriorityPill = ({ value }) => (
    <span className={cn("px-2 py-0.5 text-xs font-medium rounded-full capitalize", priorityStyles[value] || priorityStyles.medium)}>
        {value}
    </span>
);

const TrainingList = ({ defaultFilter = null, mode = "auto" }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { fetch, remove } = useApiMethods();

    const is_admin = JSON.parse(sessionStorage.getItem("is_admin")) || JSON.parse(sessionStorage.getItem("is_super_admin"));

    const isTeamView = mode === "team" || location.pathname.endsWith("/team");
    const isAdminView = !isTeamView && is_admin;

    const [tasks, setTasks] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState(defaultFilter || "");
    const [priorityFilter, setPriorityFilter] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Debounce the search input so each keystroke doesn't trigger a request.
    // Only the admin path uses search server-side; non-admin uses it client-side
    // via filteredAssignments, but the debounce also stops it from re-fetching needlessly.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 350);
        return () => clearTimeout(t);
    }, [search]);

    // Routes /training and /training/overdue resolve to the same component instance,
    // so the defaultFilter prop changes but state seeded from it at mount doesn't.
    // Re-sync whenever the route (and therefore defaultFilter) changes — this cascades
    // through loadData's deps and re-fires the GET with the right status param.
    useEffect(() => {
        setStatusFilter(defaultFilter || "");
    }, [defaultFilter, location.pathname]);

    const loadDepartments = useCallback(async () => {
        try {
            const res = await fetch(EMPLOYEE_DEPARTMENT, { dropdown: true });
            const list = res?.data || [];
            setDepartments(list.map((d) => ({ value: d.id, label: d.department_name })));
        } catch (_) { /* non-fatal */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            if (isAdminView) {
                const filters = {};
                if (statusFilter) filters.status = statusFilter;
                if (priorityFilter) filters.priority = priorityFilter;
                if (departmentFilter) filters.department = departmentFilter;
                if (debouncedSearch) filters.search = debouncedSearch;
                const res = await fetch(TRAINING_TASKS, filters);
                setTasks(res?.data || []);
            } else {
                const url = isTeamView ? TRAINING_TEAM_TASKS : TRAINING_MY_TASKS;
                const filters = {};
                if (statusFilter) filters.status = statusFilter;
                const res = await fetch(url, filters);
                setAssignments(res?.data || []);
            }
        } catch (err) {
            ToastMsg("error", "Failed to load trainings");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdminView, isTeamView, statusFilter, priorityFilter, departmentFilter, debouncedSearch]);

    useEffect(() => {
        loadData();
        if (isAdminView) loadDepartments();
    }, [loadData, loadDepartments, isAdminView]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            const res = await remove(`${TRAINING_TASKS}?id=${deleteTarget.id}`);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Training removed");
                setDeleteTarget(null);
                loadData();
            } else {
                ToastMsg("error", res?.message || "Failed to remove training");
            }
        } catch (_) {
            ToastMsg("error", "Failed to remove training");
        }
    };

    const filteredAssignments = useMemo(() => {
        if (!search) return assignments;
        const q = search.toLowerCase();
        return assignments.filter((a) => (a.title || "").toLowerCase().includes(q));
    }, [assignments, search]);

    const headerTitle = isAdminView
        ? "All Trainings"
        : isTeamView
            ? "Team Trainings"
            : "My Trainings";
    const headerSubtitle = isAdminView
        ? "Assign and track training tasks across departments"
        : isTeamView
            ? "Trainings assigned to your team"
            : "Trainings assigned to you";

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={GraduationCap}
                title={headerTitle}
                subtitle={headerSubtitle}
                totalRecords={isAdminView ? tasks.length : filteredAssignments.length}
                buttonEnable={isAdminView}
                setOpenDialog={() => navigate("/training/add")}
                buttonName={"Assign Training"}
            />

            <div className="flex flex-wrap items-center gap-2 px-3 pb-3">
                <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search by title"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="max-w-xs"
                    />
                </div>
                <Select
                    placeholder="Status"
                    allowClear
                    value={statusFilter || undefined}
                    onChange={(v) => setStatusFilter(v || "")}
                    options={[
                        { value: "pending", label: "Pending" },
                        { value: "in_progress", label: "In Progress" },
                        { value: "completed", label: "Completed" },
                        { value: "overdue", label: "Overdue" },
                    ]}
                    style={{ minWidth: 140 }}
                />
                {isAdminView && (
                    <>
                        <Select
                            placeholder="Priority"
                            allowClear
                            value={priorityFilter || undefined}
                            onChange={(v) => setPriorityFilter(v || "")}
                            options={[
                                { value: "low", label: "Low" },
                                { value: "medium", label: "Medium" },
                                { value: "high", label: "High" },
                            ]}
                            style={{ minWidth: 130 }}
                        />
                        <Select
                            placeholder="Department"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            value={departmentFilter || undefined}
                            onChange={(v) => setDepartmentFilter(v || "")}
                            options={departments}
                            style={{ minWidth: 180 }}
                        />
                    </>
                )}
            </div>

            <div className="px-3 pb-6 overflow-auto">
                {loading ? (
                    <div className="text-center py-10 text-muted-foreground">Loading...</div>
                ) : isAdminView ? (
                    <AdminTable
                        tasks={tasks}
                        onView={(id) => navigate(`/training/task/${id}`)}
                        onEdit={(id) => navigate(`/training/edit/${id}`)}
                        onDelete={(t) => setDeleteTarget(t)}
                    />
                ) : (
                    <EmployeeTable
                        assignments={filteredAssignments}
                        showEmployee={isTeamView}
                        onView={(id) => navigate(`/training/view/${id}`)}
                    />
                )}
            </div>

            <ConfirmPopupModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Remove Training"
                description={`Are you sure you want to remove "${deleteTarget?.title}"? This will deactivate it for all assignees.`}
                confirmText="Remove"
            />
        </ContainerCard>
    );
};

const AdminTable = ({ tasks, onView, onEdit, onDelete }) => {
    if (!tasks.length) {
        return <div className="text-center py-10 text-muted-foreground">No trainings found.</div>;
    }
    return (
        <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
                <thead className="bg-muted/40">
                    <tr className="text-left text-muted-foreground">
                        <th className="px-3 py-2 font-medium">Title</th>
                        <th className="px-3 py-2 font-medium">Assignees</th>
                        <th className="px-3 py-2 font-medium">Due Date</th>
                        <th className="px-3 py-2 font-medium">Priority</th>
                        <th className="px-3 py-2 font-medium">Completion</th>
                        <th className="px-3 py-2 font-medium">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {tasks.map((t) => (
                        <tr key={t.id} className="border-t border-border hover:bg-muted/20">
                            <td className="px-3 py-2 font-medium text-foreground">{t.title}</td>
                            <td className="px-3 py-2"><Badge variant="secondary"><Users className="h-3 w-3 mr-1" />{t.total_assignees}</Badge></td>
                            <td className="px-3 py-2">{t.due_date ? dayjs(t.due_date).format("DD-MM-YYYY") : "-"}</td>
                            <td className="px-3 py-2"><PriorityPill value={t.priority} /></td>
                            <td className="px-3 py-2">{t.completed_count}/{t.total_assignees} ({t.completion_percent}%)</td>
                            <td className="px-3 py-2">
                                <div className="flex items-center gap-1">
                                    <Button size="sm" variant="ghost" onClick={() => onView(t.id)}><Eye className="h-4 w-4" /></Button>
                                    <Button size="sm" variant="ghost" onClick={() => onEdit(t.id)}><Edit className="h-4 w-4" /></Button>
                                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600" onClick={() => onDelete(t)}><Trash className="h-4 w-4" /></Button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const EmployeeTable = ({ assignments, showEmployee, onView }) => {
    if (!assignments.length) {
        return <div className="text-center py-10 text-muted-foreground">No trainings assigned.</div>;
    }
    return (
        <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
                <thead className="bg-muted/40">
                    <tr className="text-left text-muted-foreground">
                        <th className="px-3 py-2 font-medium">Title</th>
                        {showEmployee && <th className="px-3 py-2 font-medium">Employee</th>}
                        <th className="px-3 py-2 font-medium">Due Date</th>
                        <th className="px-3 py-2 font-medium">Priority</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {assignments.map((a) => (
                        <tr key={a.id} className="border-t border-border hover:bg-muted/20">
                            <td className="px-3 py-2 font-medium text-foreground">{a.title}</td>
                            {showEmployee && <td className="px-3 py-2">{a.employee_name}</td>}
                            <td className="px-3 py-2">{a.due_date ? dayjs(a.due_date).format("DD-MM-YYYY") : "-"}</td>
                            <td className="px-3 py-2"><PriorityPill value={a.priority} /></td>
                            <td className="px-3 py-2"><StatusPill value={a.effective_status} /></td>
                            <td className="px-3 py-2">
                                <Button size="sm" variant="ghost" onClick={() => onView(a.id)}><Eye className="h-4 w-4" /></Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default TrainingList;
